import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Toast } from "@/ui-kit";
import { InspectorsRepository } from "@/api/inspectors/repository";
import { InspectorsService } from "@/api/inspectors/service";
import type { IInspectorHud } from "@/types/users-inspectors";
import { serverErrorHandler } from "@/utils/serverErrorHandler";

type IHudFormData = {
  hudIdNumber: string;
  hudIdIssuanceDate: string;
  hudIdExpiryDate: string;
  hudFrontPicture: File[];
  hudFrontPictureUrl?: string;
  hudBackPicture: File[];
  hudBackPictureUrl?: string;
};

type IUseHudFormOptions = {
  userId: number;
  hudDetail?: IInspectorHud;
  onSuccess: (response: IInspectorHud) => void;
};

export const useHudForm = ({
  userId,
  hudDetail,
  onSuccess,
}: IUseHudFormOptions) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const schema = useMemo(() => {
    return z.object({
      hudIdNumber: z.string().min(1, { message: t("validation.required") }),
      hudIdIssuanceDate: z
        .string()
        .min(1, { message: t("validation.required") }),
      hudIdExpiryDate: z.string().min(1, { message: t("validation.required") }),
      hudFrontPicture: z
        .array(z.instanceof(File))
        .refine((files) => files.length > 0, {
          message: t("validation.required"),
        }),
      hudBackPicture: z
        .array(z.instanceof(File))
        .refine((files) => files.length > 0, {
          message: t("validation.required"),
        }),
    });
  }, [t]);

  const defaultValues = useMemo((): IHudFormData => {
    return {
      hudIdNumber: hudDetail?.documentNumber?.toString() ?? "",
      hudIdIssuanceDate: hudDetail?.issuanceDate ?? "",
      hudIdExpiryDate: hudDetail?.expiryDate ?? "",
      hudFrontPicture: [],
      hudFrontPictureUrl: hudDetail?.frontImageUrl,
      hudBackPicture: [],
      hudBackPictureUrl: hudDetail?.backImageUrl,
    };
  }, [hudDetail]);

  const form = useForm<IHudFormData>({
    defaultValues,
    resolver: zodResolver(schema),
  });

  const { handleSubmit } = form;

  const onSubmit = useCallback(() => {
    handleSubmit((formData: IHudFormData) => {
      setLoading(true);
      const repository = new InspectorsRepository(new InspectorsService());

      repository
        .upsertHudDetails({
          hudDetail: {
            userId,
            issuanceDate: formData.hudIdIssuanceDate,
            expiryDate: formData.hudIdExpiryDate,
            documentNumber: formData.hudIdNumber,
          },
          frontImage: formData.hudFrontPicture[0],
          backImage: formData.hudBackPicture[0],
        })
        .then((response) => {
          Toast.success({
            title: t("toast.what_updated", { what: t("label.hud_details") }),
          });
          onSuccess(response);
        })
        .catch(serverErrorHandler())
        .finally(() => {
          setLoading(false);
        });
    })();
  }, [handleSubmit, userId, onSuccess, t]);

  return { form, onSubmit, loading };
};
